import React, { useMemo, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Plus, Trash, Sparkle, Globe, ArrowRight } from '@phosphor-icons/react';
import TimezoneSelect from '../components/TimezoneSelect';
import {
  detectBrowserTimezone, getWallClock, timeLabelInTz, wallClockToUTC,
} from '../lib/tz';

/**
 * Group Time Finder — pick N people in different timezones with their working
 * hours and find the overlapping window where everyone is available.
 *
 * Algorithm: for each upcoming day (next 14), iterate every 30-min slot. For
 * each participant, check if the projection of that UTC instant into their
 * timezone falls inside their declared working window. If true for everyone,
 * mark the slot green.
 */

const DEFAULT_DURATION = 60; // minutes — must be reachable for everyone
const SLOT_STEP_MIN    = 30;
const DAYS_AHEAD       = 14;

function defaultParticipant(label) {
  return {
    id: Math.random().toString(36).slice(2, 8),
    label,
    tz: detectBrowserTimezone(),
    start: '09:00',
    end:   '17:00',
  };
}

function parseHM(s) {
  const [h, m] = (s || '').split(':').map((x) => parseInt(x, 10));
  if (isNaN(h) || isNaN(m)) return null;
  return h * 60 + m;
}

function pointInWindow(minutes, startStr, endStr) {
  const s = parseHM(startStr);
  const e = parseHM(endStr);
  if (s == null || e == null) return false;
  if (e <= s) return false;
  return minutes >= s && minutes < e;
}

export default function GroupTimeFinderPage() {
  const [participants, setParticipants] = useState(() => [
    defaultParticipant('You'),
    { ...defaultParticipant('Counterpart'), tz: 'America/New_York' },
  ]);
  const [duration, setDuration] = useState(DEFAULT_DURATION);
  const [referenceTz, setReferenceTz] = useState(() => detectBrowserTimezone());

  const addParticipant = () => {
    setParticipants((cur) => [...cur, defaultParticipant(`Person ${cur.length + 1}`)]);
  };
  const removeParticipant = (id) => {
    setParticipants((cur) => (cur.length > 2 ? cur.filter((p) => p.id !== id) : cur));
  };
  const updateParticipant = (id, patch) => {
    setParticipants((cur) => cur.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  };

  // Generate the grid: 14 days × every 30 min. For each slot, decide green/grey.
  const daysGrid = useMemo(() => {
    const today = new Date();
    today.setSeconds(0, 0);
    // Round up to the next half-hour for the start of "today's row".
    const todayStart = new Date(today);
    todayStart.setHours(0, 0, 0, 0);

    const out = [];
    for (let dayIdx = 0; dayIdx < DAYS_AHEAD; dayIdx++) {
      const dayStart = new Date(todayStart);
      dayStart.setDate(dayStart.getDate() + dayIdx);
      // Slots: 00:00..24:00 step 30 min in the REFERENCE tz (for layout)
      const slots = [];
      // We anchor the slot grid to the wall-clock of `referenceTz`. To do
      // that cleanly, we walk the reference day in 30-min steps.
      // The day in referenceTz is identified by the wall-clock of `dayStart`
      // — close enough for the next 14 days.
      const refWC = getWallClock(dayStart, referenceTz);
      for (let minutes = 0; minutes < 24 * 60; minutes += SLOT_STEP_MIN) {
        // Build a UTC instant matching the reference wall-clock at this minute.
        // wallClockToUTC handles DST too.
        const instant = wallClockToUTC(
          refWC.year, refWC.month, refWC.day,
          Math.floor(minutes / 60), minutes % 60, referenceTz,
        );

        // For each participant, project the instant into their tz and check
        // their working window.
        const slotMinutesNeeded = duration;
        let everyoneAvailable = true;
        const tzClocks = [];
        for (const p of participants) {
          let ok = true;
          // Check the whole duration window — sample start, +duration-1
          for (const offset of [0, slotMinutesNeeded - 1]) {
            const probe = new Date(instant.getTime() + offset * 60000);
            const wc = getWallClock(probe, p.tz);
            const wcMin = wc.hour * 60 + wc.minute;
            if (!pointInWindow(wcMin, p.start, p.end)) { ok = false; break; }
          }
          tzClocks.push({ p, label: timeLabelInTz(instant, p.tz) });
          if (!ok) everyoneAvailable = false;
        }
        slots.push({
          instant, minutes,
          label: timeLabelInTz(instant, referenceTz),
          everyoneAvailable,
          tzClocks,
        });
      }
      // For "today", drop slots that are already in the past.
      const filtered = dayIdx === 0 ? slots.filter((s) => s.instant >= today) : slots;
      out.push({
        date: dayStart,
        label: dayStart.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }),
        slots: filtered,
        anyGreen: filtered.some((s) => s.everyoneAvailable),
      });
    }
    return out;
  }, [participants, duration, referenceTz]);

  return (
    <div className="space-y-6" data-testid="group-time-finder">
      <div>
        <div className="flex items-center gap-2 text-[#002677]">
          <Globe size={20} weight="duotone" />
          <span className="text-xs font-semibold uppercase tracking-[0.25em]">Free tool</span>
        </div>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Group time finder</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Find the overlap when everyone&apos;s working hours align — green slots are when the whole group is free.
        </p>
      </div>

      <Card className="rounded-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-semibold text-lg">Participants</h2>
          <Button onClick={addParticipant} size="sm"
                  className="rounded-sm bg-[#002677] hover:bg-[#002677]/90"
                  data-testid="gtf-add-participant">
            <Plus size={14} weight="bold" className="mr-1.5" /> Add person
          </Button>
        </div>
        <div className="space-y-2.5">
          {participants.map((p) => (
            <div key={p.id} className="grid grid-cols-12 gap-2 items-center"
                 data-testid={`gtf-participant-${p.id}`}>
              <Input
                value={p.label}
                onChange={(e) => updateParticipant(p.id, { label: e.target.value })}
                className="col-span-3 rounded-sm h-9"
                placeholder="Name"
              />
              <div className="col-span-5">
                <TimezoneSelect
                  value={p.tz}
                  onChange={(tz) => updateParticipant(p.id, { tz })}
                  testId={`gtf-tz-${p.id}`}
                  className="w-full"
                />
              </div>
              <Input
                type="time" value={p.start}
                onChange={(e) => updateParticipant(p.id, { start: e.target.value })}
                className="col-span-1 rounded-sm h-9 px-2 text-xs"
              />
              <Input
                type="time" value={p.end}
                onChange={(e) => updateParticipant(p.id, { end: e.target.value })}
                className="col-span-1 rounded-sm h-9 px-2 text-xs"
              />
              <div className="col-span-2 flex justify-end">
                {participants.length > 2 && (
                  <Button onClick={() => removeParticipant(p.id)} size="sm"
                          variant="ghost" className="h-8 rounded-sm text-destructive hover:text-destructive">
                    <Trash size={14} />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="rounded-sm p-5">
        <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-4">
          <div className="flex-1 space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
              Meeting duration (minutes)
            </Label>
            <Input
              type="number" min="15" max="240" step="15" value={duration}
              onChange={(e) => setDuration(Number(e.target.value) || 60)}
              className="rounded-sm h-9 w-32"
              data-testid="gtf-duration"
            />
          </div>
          <div className="flex-1 space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
              Display time in
            </Label>
            <TimezoneSelect
              value={referenceTz}
              onChange={setReferenceTz}
              testId="gtf-reference-tz"
              className="w-full max-w-[260px]"
            />
          </div>
        </div>

        <div className="text-xs text-muted-foreground mb-3 inline-flex items-center gap-3">
          <span className="inline-flex items-center gap-1">
            <span className="h-3 w-3 rounded-sm bg-[#00B140]" /> Everyone&apos;s free
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-3 w-3 rounded-sm bg-muted border border-border" /> Not all free
          </span>
        </div>

        <div className="space-y-3">
          {daysGrid.map((day) => (
            <div key={day.label}>
              <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-1.5">
                {day.label} {!day.anyGreen && <span className="text-muted-foreground/60 font-normal">· no overlap</span>}
              </div>
              <div className="flex flex-wrap gap-1">
                {day.slots.map((s, i) => (
                  <div
                    key={i}
                    title={s.tzClocks.map((c) => `${c.p.label}: ${c.label}`).join(' · ')}
                    className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${
                      s.everyoneAvailable
                        ? 'bg-[#00B140] border-[#00B140] text-white cursor-help font-semibold'
                        : 'bg-muted/40 border-border text-muted-foreground/60'
                    }`}
                  >
                    {s.label}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 pt-4 border-t border-border flex items-center gap-2 text-xs text-muted-foreground">
          <Sparkle size={14} className="text-[#002677]" />
          Hover a green slot to see what time it is for each participant.
          <ArrowRight size={12} />
          Pick one and create the appointment in <a href="/schedule" className="text-[#002677] hover:underline font-medium">Schedule &amp; Remind</a>.
        </div>
      </Card>
    </div>
  );
}
